import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Headphones, ArrowDownRight, Sparkles, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PODCAST_COVER =
    "/assets/podcast-cover.png";

export const Hero = () => {
    const [count, setCount] = useState(null);
    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/stats`)
            .then((r) => mounted && setCount(r.data?.subscribers ?? null))
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-12 md:pt-20 pb-24 overflow-hidden grain"
        >
            <div className="aurora" aria-hidden />
            <div
                className="absolute inset-0 -z-10 bg-gradient-to-b from-lilac-50 via-[#F4ECF7] to-[#F4ECF7]"
                aria-hidden
            />

            <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                <div className="grid grid-cols-12 gap-8 lg:gap-12 items-center">
                    <div className="col-span-12 lg:col-span-7">
                        <div className="eyebrow text-gold-deep flex items-center gap-3 mb-8 reveal">
                            <Sparkles className="w-3.5 h-3.5" /> Hosted by Coach Melissa · Faith-Centered
                        </div>
                        <h1
                            className="font-display text-[44px] sm:text-6xl lg:text-[88px] leading-[0.98] tracking-tight text-lilac-900 reveal"
                            data-testid="hero-title"
                        >
                            Becoming <span className="italic gold-text">H.E.R.</span>
                            <br />
                            <span className="text-lilac-700/90">Breaking the</span>{" "}
                            <span className="italic">Cycles.</span>
                        </h1>

                        <p className="mt-8 max-w-xl text-base md:text-lg text-lilac-700 leading-relaxed reveal" style={{ animationDelay: "120ms" }}>
                            A faith-centered podcast for women ready to heal, grow, and walk boldly in
                            the woman God created them to be — breaking toxic patterns, overcoming people
                            pleasing, healing identity wounds, rebuilding self-worth, and learning how to
                            stop shrinking in places God called you to rise.
                        </p>

                        <p className="mt-5 max-w-xl text-sm md:text-base text-lilac-700/80 italic reveal" style={{ animationDelay: "180ms" }}>
                            This is more than a podcast. This is a journey of becoming. <span className="not-italic">💜</span>
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-4 reveal" style={{ animationDelay: "240ms" }}>
                            <Button
                                data-testid="hero-cta-listen"
                                asChild
                                className="rounded-full h-12 px-7 bg-lilac-900 hover:bg-lilac-700 text-white text-sm shadow-[0_10px_30px_rgba(43,29,53,0.18)]"
                            >
                                <a href="#episodes">
                                    <Headphones className="w-4 h-4" /> See the Episodes
                                </a>
                            </Button>
                            <Button
                                data-testid="hero-cta-about"
                                variant="outline"
                                asChild
                                className="rounded-full h-12 px-7 border-lilac-300 bg-white/60 hover:bg-white text-lilac-900"
                            >
                                <a href="#host">
                                    Meet Coach Melissa <ArrowDownRight className="w-4 h-4" />
                                </a>
                            </Button>
                        </div>

                        {count !== null && (
                            <div
                                data-testid="hero-waitlist-counter"
                                className="mt-8 inline-flex items-center gap-3 rounded-full glass px-5 py-2.5 reveal"
                                style={{ animationDelay: "320ms" }}
                            >
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gold" />
                                </span>
                                <Users className="w-4 h-4 text-lilac-700" />
                                <span className="text-sm text-lilac-900">
                                    <strong className="font-display tabular-nums" data-testid="waitlist-count">
                                        {count.toLocaleString()}
                                    </strong>{" "}
                                    <span className="text-lilac-700">women already in the becoming</span>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Right: actual podcast cover art */}
                    <div className="col-span-12 lg:col-span-5 reveal" style={{ animationDelay: "320ms" }}>
                        <div className="relative">
                            <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-gold/20 blur-3xl" />
                            <div className="absolute -bottom-12 -right-8 w-48 h-48 rounded-full bg-lilac-300/40 blur-3xl" />
                            <div className="relative rounded-[36px] overflow-hidden border border-border shadow-[0_40px_80px_rgba(43,29,53,0.18)] animate-float-slow">
                                <img
                                    src={PODCAST_COVER}
                                    alt="Becoming H.E.R. — Breaking the Cycles podcast cover"
                                    className="w-full h-auto object-cover"
                                    data-testid="hero-cover-art"
                                />
                            </div>
                            <div className="absolute -bottom-5 left-6 glass rounded-full px-5 py-2.5 shadow-[0_10px_30px_rgba(43,29,53,0.10)] flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                                <span className="eyebrow text-lilac-900">Now streaming</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Marquee */}
                <div className="mt-24 overflow-hidden border-y border-border/70 py-5">
                    <div className="flex w-max marquee-track gap-12 whitespace-nowrap">
                        {Array.from({ length: 2 }).map((_, k) => (
                            <div key={k} className="flex gap-12 items-center">
                                {[
                                    "Break Toxic Cycles",
                                    "Heal Deep Wounds",
                                    "Restore Your Identity",
                                    "Walk in Purpose",
                                    "Real Conversations",
                                    "Raw Healing",
                                    "Kingdom Transformation",
                                    "No More Shrinking",
                                ].map((t) => (
                                    <span
                                        key={t + k}
                                        className="eyebrow text-lilac-700 flex items-center gap-3"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                                        {t}
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
